import React, { useEffect } from "react";
import { Button, Tooltip, Row, Col } from "antd";
import { useRecorder } from "../context/recorderService";
import Messages from "./Messages.jsx";
import play_icon from "../img/aufnehmen_icons/play.svg";
import pause_icon from "../img/aufnehmen_icons/pause.svg";
import record_icon from "../img/aufnehmen_icons/record.svg";
import stop_icon from "../img/aufnehmen_icons/stop.svg";
import reset_icon from "../img/aufnehmen_icons/reset.svg";
import streenTheme from "../css/theme.scss";


const verbose = true; // set to true to enable debug logging
const vlog = (...args) => verbose && console.log(...args);

  // Reset resetMode when a new recording is made
  

const AufnehmenPanel = ({
  storyId,
  sentenceId,
  userId,
  groupId,
  visibility,
  sentenceText,
  hasRecordingRights,
  hasFeedbackRights,
  onRecordingSaved,
  resetMode = false,
  onResetModeChange,
}) => {
  const { showMessageSuccess, messageHolder, showErrorMessage } = Messages();
  vlog("AufnehmenPanel component rendering");
  const recorderData = useRecorder({
    storyId,
    sentenceId,
    userId,
    groupId,
    visibility,
  });

  // Debug logging
  vlog("StudioRecorderPanel: recorderData =", recorderData);
  vlog("StudioRecorderPanel: waveformData =", recorderData.waveformData);

  const {
    isRecording,
    audioUrl,
    audioList,
    loading,
    isPlaying,
    waveformData,
    recordingDeleted,
    startRecording,
    stopRecording,
    resetRecording,
    pausePlayback,
    saveRecording,
    playLocalRecording,
    playExistingRecording,
    // For animated playback visualization
    animatedWaveformData, // Will be undefined if not available
  } = recorderData;

  // Fallback to static waveform if animated waveform is not available
  const currentWaveform =
    isPlaying && animatedWaveformData ? animatedWaveformData : waveformData;

  useEffect(() => {
    return () => {
      pausePlayback();
    };
  }, [pausePlayback]);

  const hasExistingRecording = audioList.length > 0 && !resetMode && !loading;
  const handleMainAction = async () => {
    vlog(
      "handleMainAction called, isRecording:",
      isRecording,
      "audioUrl:",
      audioUrl,
      "isPlaying:",
      isPlaying,
    );

    if (!hasRecordingRights && !showPlayButton && !isRecording) {
      showErrorMessage("Sie haben keine Rechte, für diesen Satz aufzunehmen.");
      return;
    }
    if (isRecording) {
      vlog("Calling handleStopAndSave");
      return await handleStopAndSave();
    }

    if (showPlayButton) {
      if (isPlaying) {
        vlog("Calling pausePlayback");
        return pausePlayback();
      }
      // Play either the new recording or the existing one
      if (audioUrl) {
        vlog("Calling playLocalRecording");
        return playLocalRecording(audioUrl);
      } else if (hasExistingRecording) {
        vlog("Calling playExistingRecording");
        return playExistingRecording(audioList[0].file_path);
      }
    }

    vlog("Calling startRecording");
    try {
      return await startRecording();
    } catch (error) {
      console.error("Fehler beim Starten der Aufnahme", error);
      // Check if it's a microphone permission error
      if (error.name === "NotAllowedError") {
        showErrorMessage("Mikrofonzugriff verweigert. Bitte erlauben Sie den Zugriff auf das Mikrofon.");
      } else if (error.name === "NotFoundError") {
        showErrorMessage("Kein Mikrofon gefunden. Bitte schließen Sie ein Mikrofon an.");
      } else {
        showErrorMessage("Fehler beim Starten der Aufnahme: " + error.message);
      }
      // Don't re-throw to prevent unhandled promise rejection since we've already handled it for the user
    }
  };
  // Exit reset mode when showing recordings
  useEffect(() => {
    if (resetMode && (audioUrl || hasExistingRecording) && onResetModeChange) {
      // Exit reset mode when we have a recording to show
      // Add a small delay to ensure smooth transition
      const timer = setTimeout(() => {
        onResetModeChange(false);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [audioUrl, hasExistingRecording, resetMode, onResetModeChange]);
  // Reset recordingDeleted flag when audioList changes
  useEffect(() => {
    if (audioList.length > 0 && recordingDeleted) {
      // There are recordings now, so reset the deleted flag
      // This is handled in the fetchRecordings function, but we can also do it here
    }
  }, [audioList, recordingDeleted]);

  // Determine if we have an existing recording to play
  // Don't show play button if a recording was just deleted
  const showPlayButton = (audioUrl || hasExistingRecording) && !isRecording && !recordingDeleted;

  const mainButtonIcon = isRecording ? (
    <img src={stop_icon} alt="" aria-hidden="true" style={{ height: 32, display: "block" }} />
  ) : showPlayButton ? (
    isPlaying ? (
      <img src={pause_icon} alt="" aria-hidden="true" style={{ height: 32, display: "block" }} />
    ) : (
      <img src={play_icon} alt="" aria-hidden="true" style={{ height: 32, display: "block" }} />
    )
  ) : (
    <img src={record_icon} alt="" aria-hidden="true" style={{ height: 32, display: "block" }} />
  );

  const mainButtonLabel = isRecording
    ? "Aufnahme stoppen und speichern"
    : showPlayButton
      ? isPlaying
        ? "Wiedergabe pausieren"
        : hasExistingRecording && !audioUrl
          ? "Vorhandene Aufnahme wiedergeben"
          : "Aufnahme wiedergeben"
      : "Aufnahme starten";

  const handleSave = async (recordingBlob) => {
    if (!recordingBlob) return;
    try {
      await saveRecording(recordingBlob);
      showMessageSuccess("Aufnahme erfolgreich gespeichert!");
      if (onRecordingSaved) {
        onRecordingSaved(recordingBlob);
      }
      // Don't reset automatically after saving - let user decide to reset or make a new recording
    } catch (error) {
      console.error("Fehler beim Speichern der Aufnahme", error);
      showErrorMessage("Fehler beim Speichern der Aufnahme");
    }
  };

  // Handle recording stop and auto-save
  const handleStopAndSave = async () => {
    vlog("Stopping recording and saving automatically");
    // stopRecording should return the recording blob
    const recordingBlob = await stopRecording();
    vlog("Recording stopped, blob:", recordingBlob);
    // Auto-save after stopping
    if (recordingBlob) {
      await handleSave(recordingBlob);
    }
  };
  
  return (
    <Row className="aufnehmen-recorder-panel" gutter={[18, 18]}>
      {messageHolder}
      <Col xs={24} sm={24} md={12} className="aufnehmen-recorder-left">
        <div className="studio-left-inner">
          <div className="studio-waveform">
            {vlog("Rendering waveform with data:", currentWaveform) || true}
            {waveformData && waveformData.length > 0 ? (
              currentWaveform.map((level, index) => {
                const heightPercent = Math.max(2, level);
                const heightPx = Math.max(3, (heightPercent / 100) * 80); // Scale to max 80px
                vlog(
                  `Rendering wavebar ${index} with height:`,
                  `${heightPercent}%`,
                );
                return (
                  <div
                    key={index}
                    className={`aufnehmen-studio-wavebar aufnehmen-studio-wavebar-${index + 1}`}
                    style={{
                      height: `${heightPx}px`,
                    }}
                    data-height={heightPercent}
                    data-height-px={heightPx}
                  />
                );
              })
            ) : (
              <div className="waveform-placeholder">Bereit zur Aufnahme</div>
            )}
          </div>

          <Row className="studio-mic-area">
            {/* <Col span={3} style={{paddingLeft: '8px'}}>
              {showPlayButton && (
                <Button
                  shape="circle"
                  className="studio-side-button"
                  icon={<img src={reset_icon} alt="" aria-hidden="true" style={{ height: 29, display: "block" }} />}
                  onClick={() => {
                    resetRecording();
                    if (onResetModeChange) {
                      onResetModeChange(true);
                    }
                  }}
                  aria-label="Zurücksetzen"
                />
              )}
            </Col> */}
            <Col style={{ justifyContent: "center" }} >
              

              <Tooltip title={mainButtonLabel}>
                <Button
                  shape="circle"
                  className="studio-mic-button"
                  style={{ backgroundColor: isRecording || isPlaying ? streenTheme.orange4 : streenTheme.white }}
                  onClick={async () => {
                    vlog("Mic button clicked");
                    try {
                      await handleMainAction();
                    } catch (error) {
                      // Error is already handled in handleMainAction, but catch to prevent unhandled rejection
                      console.debug("Error handled in handleMainAction:", error);
                    }
                  }}
                  disabled={!hasRecordingRights && !showPlayButton && !isRecording}
                  aria-pressed={isRecording || isPlaying}
                  aria-label={mainButtonLabel}
                >
                  {mainButtonIcon}
                </Button>
              </Tooltip>

              {/* <Tooltip title="Aufnahme zurücksetzen">
                <Button
                  shape="circle"
                  className="studio-side-button"
                  icon={<UndoOutlined />}
                  onClick={() => resetRecording()}
                  disabled={isRecording}
                  aria-label="Zurücksetzen"
                />
              </Tooltip> */}
            </Col>
          </Row>

          {/* <div className="aufnehmen-recorder-actions-row">
            <Space className="aufnehmen-recorder-actions">
              <Tooltip title={isRecording ? "Aufnahme stoppen" : "Aufnahme starten"}>
                <Button
                  type={isRecording ? "primary" : "default"}
                  icon={isRecording ? <StopOutlined /> : <AudioOutlined />}
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={!hasRecordingRights}
                  className="aufnehmen-recorder-button"
                >
                  {isRecording ? "Stopp" : "Start"}
                </Button>
              </Tooltip>

              <Tooltip title={activeRecording ? "Aufnahme wiedergeben" : "Keine Aufnahme verfügbar"}>
                <Button
                  type="default"
                  icon={<PlayCircleFilled />}
                  onClick={() => audioUrl && playLocalRecording(audioUrl)}
                  disabled={!audioUrl}
                  className="aufnehmen-recorder-button"
                >
                  Abspielen
                </Button>
              </Tooltip>

              <Tooltip title={canSave ? "Aufnahme speichern" : "Keine neue Aufnahme zum Speichern"}>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={handleSave}
                  disabled={!canSave}
                  className="aufnehmen-recorder-button"
                >
                  Speichern
                </Button>
              </Tooltip>
            </Space>
          </div> */}

          {/* {loading && <Text type="secondary">Lade Aufnahmen...</Text>} */}

          {/* {hasExistingRecording && !audioUrl && (
            <div className="studio-existing-recording">
              <Text strong>Vorhandene Aufnahme</Text>
            </div>
          )} */}
        </div>
      </Col>

      <Col xs={24} sm={24} md={12} className="aufnehmen-recorder-right">
        {/* <FeedbackGeben 
          audioText={sentenceText}
          userId={userId}
        />
        <Button
          className="studio-feedback-button"
          icon={<CommentOutlined />}
          block
          onClick={() => vlog("Sich selbst Feedback geben")}
        >
          Sich selbst Feedback geben
        </Button>
        <Button
          className="studio-feedback-button"
          icon={<MessageOutlined />}
          block
          onClick={() => vlog("Rückmeldung zeigen")}
        >
          Rückmeldung zeigen
        </Button>
        {hasFeedbackRights && (
          <Button
            className="studio-feedback-help-button"
            icon={<ReloadOutlined />}
            block
            onClick={() => vlog("Feedback aktualisieren")}
          >
            Feedback aktualisieren
          </Button>
        )} */}
      </Col>
    </Row>
  );
};

export default AufnehmenPanel;
